import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  useTheme,
  alpha,
  Chip,
  Grid,
  Skeleton,
} from '@mui/material';
import {
  AccessTime as AccessTimeIcon, // Icon for Avg Talk Time
  Phone as PhoneIcon, // Icon for Total Calls
  CallMade as CallMadeIcon, // Icon for Outgoing
  CallReceived as CallReceivedIcon, // Icon for Incoming
  People as PeopleIcon, // Icon for Unique Connected Leads
  ContactPhone as ContactPhoneIcon, // Icon for Total Contacted Leads
} from '@mui/icons-material';
import { useAgentReport } from '../../../contexts/AgentReportContext';

const CallPerformanceSummary = ({ dateRangeState, periodType }) => { // Removed color prop as it will be dynamic for each card
  const theme = useTheme();
  const navigate = useNavigate();
  const { fetchAgentCallPerformanceSummary } = useAgentReport();
  const [overviewData, setOverviewData] = useState({});
  const [isCardsLoading, setIsCardsLoading] = useState(true);

  const defaultMetrics = [
    {
      id: 'totalCalls',
      title: 'Total Calls',
      value: 0,
      subtitle: 'Fresh: 0\nFollow-ups: 0\nNot Connected: 0',
      icon: <PhoneIcon />,
      color: 'primary',
    },
    {
      id: 'totalOutgoing',
      title: 'Total Outgoing',
      value: 0,
      icon: <CallMadeIcon />,
      color: 'primary',
    },
    {
      id: 'totalIncoming',
      title: 'Total Incoming',
      value: 0,
      icon: <CallReceivedIcon />,
      color: 'info',
    },
    {
      id: 'uniqueConnectedLeads',
      title: 'Unique Connected Leads',
      value: 0,
      icon: <PeopleIcon />,
      color: 'success',
    },
    {
      id: 'totalTalkTime',
      title: 'Total Talk Time',
      value: '0m 0s',
      icon: <AccessTimeIcon />,
      color: 'info',
    },
    {
      id: 'totalContactedLeads',
      title: 'Total Contacted Leads',
      value: 0,
      icon: <ContactPhoneIcon />,
      color: 'primary',
    },
  ];

  const [metrics, setMetrics] = useState(defaultMetrics);

  useEffect(() => {
    fetchCallOverview();
  }, [dateRangeState, periodType]);

  const handleCardClick = (metricId) => {
    // Build URL params for navigation
    const params = new URLSearchParams();
    
    // Add date range filters
    if (dateRangeState) {
      if (dateRangeState.filter?.id && dateRangeState.filter.id !== 'All') {
        params.set('date_filter', dateRangeState.filter.id);
      }
      
      // Always pass start and end dates if they exist
      if (dateRangeState.currentStart && dateRangeState.currentEnd) {
        params.set('start_date', dateRangeState.currentStart.format('YYYY-MM-DD'));
        params.set('end_date', dateRangeState.currentEnd.format('YYYY-MM-DD'));
      }
    }
    
    // Handle navigation for leads cards (Contacted Leads and Connected Leads)
    if (metricId === 'totalContactedLeads') {
      params.set('stat_type', 'contacted-leads');
      // Navigate to leads page with filters
      navigate(`/leads?${params.toString()}`, {
        state: { 
          from: 'reports',
          dashboardFilter: dateRangeState?.filter?.id,
          dashboardStartDate: dateRangeState?.currentStart?.format('YYYY-MM-DD'),
          dashboardEndDate: dateRangeState?.currentEnd?.format('YYYY-MM-DD')
        }
      });
      return;
    }
    
    if (metricId === 'uniqueConnectedLeads') {
      params.set('stat_type', 'connected-leads');
      // Navigate to leads page with filters
      navigate(`/leads?${params.toString()}`, {
        state: { 
          from: 'reports',
          dashboardFilter: dateRangeState?.filter?.id,
          dashboardStartDate: dateRangeState?.currentStart?.format('YYYY-MM-DD'),
          dashboardEndDate: dateRangeState?.currentEnd?.format('YYYY-MM-DD')
        }
      });
      return;
    }
    
    // Handle navigation for call cards (Total Calls, Outgoing, Incoming)
    if (!['totalCalls', 'totalOutgoing', 'totalIncoming'].includes(metricId)) {
      return;
    }
    
    // Add call direction filter based on card clicked
    if (metricId === 'totalOutgoing') {
      params.set('call_direction', 'outgoing');
    } else if (metricId === 'totalIncoming') {
      params.set('call_direction', 'incoming');
    }
    // For totalCalls, no direction filter is needed (shows all calls)
    
    // Add lead_type filter to show only lead calls
    params.set('lead_type', 'lead_calls');
    
    // Navigate to calls page with filters
    navigate(`/calls?${params.toString()}`);
  };

  const fetchCallOverview = async () => {
    setIsCardsLoading(true);
    try {
      const startDate = dateRangeState.currentStart.format('YYYY-MM-DD');
      const endDate = dateRangeState.currentEnd.format('YYYY-MM-DD');
      const data = await fetchAgentCallPerformanceSummary('overview', startDate, endDate);
      setOverviewData(data);

      setMetrics([
        {
          id: 'totalCalls',
          title: 'Total Calls',
          value: data.total_calls || 0,
          subtitle: `Fresh: ${data.new_calls || 0}\nFollow-ups: ${data.follow_up_calls || 0}\nNot Connected: ${data.not_connected_calls || 0}`,
          icon: <PhoneIcon />,
          color: 'primary',
          clickable: true,
        },
        {
          id: 'totalOutgoing',
          title: 'Total Outgoing',
          value: data.total_outgoing || 0,
          icon: <CallMadeIcon />,
          color: 'primary',
          clickable: true,
        },
        {
          id: 'totalIncoming',
          title: 'Total Incoming',
          value: data.total_incoming || 0,
          icon: <CallReceivedIcon />,
          color: 'info',
          clickable: true,
        },
        {
          id: 'totalTalkTime',
          title: 'Total Talk Time',
          value: data.total_talk_time || '0m 0s',
          icon: <AccessTimeIcon />,
          color: 'info',
        },
        {
          id: 'totalContactedLeads',
          title: 'Contacted Leads',
          value: data.total_contacted_leads || 0,
          icon: <ContactPhoneIcon />,
          color: 'primary',
          clickable: true,
        },
        {
          id: 'uniqueConnectedLeads',
          title: 'Connected Leads',
          value: data.unique_connected_leads || 0,
          icon: <PeopleIcon />,
          color: 'success',
          clickable: true,
        },
      ]);

    } catch (error) {
      console.error('Error fetching call performance data:', error);
      setOverviewData({});
      setMetrics(defaultMetrics);
    } finally {
      setIsCardsLoading(false);
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 'bold',
            color: 'text.primary',
            mb: 1,
          }}
        >
          Call Performance Summary
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            mb: 2,
          }}
        >
          Volume, Mix & Response
        </Typography>
      </Box>
      <Grid container spacing={3}>
        {metrics.map((metric) => (
          <Grid item xs={12} sm={6} md={6} key={metric.id}>
            <Paper
              elevation={0}
              onClick={() => metric.clickable && handleCardClick(metric.id)}
              sx={{
                p: 3,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette[metric.color].main, 0.2)}`,
                background: 'background.paper',
                boxShadow: `0 2px 8px ${alpha(theme.palette[metric.color].main, 0.08)}`,
                transition: 'all 0.3s ease',
                height: '100%',
                cursor: metric.clickable ? 'pointer' : 'default',
                '&:hover': {
                  boxShadow: `0 4px 12px ${alpha(theme.palette[metric.color].main, 0.2)}`,
                  transform: metric.clickable ? 'translateY(-2px)' : 'none',
                  border: `1px solid ${alpha(theme.palette[metric.color].main, 0.4)}`,
                },
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                  {isCardsLoading ? (
                    <>
                      <Skeleton variant="text" width="60%" height={16} sx={{ mb: 1 }} />
                      <Skeleton variant="text" width="40%" height={32} sx={{ mb: 0.5 }} />
                      <Skeleton variant="text" width="80%" height={14} />
                      {metric.chip && (
                        <Skeleton
                          variant="rectangular"
                          width={140}
                          height={24}
                          sx={{ mt: 2, borderRadius: '999px' }}
                        />
                      )}
                    </>
                  ) : (
                    <>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      mb: 1,
                    }}
                  >
                    {metric.title}
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 'bold',
                      color: theme.palette[metric.color].main,
                      mb: 0.5,
                      fontSize: '1.75rem',
                    }}
                  >
                    {metric.value}
                  </Typography>
                  {metric.subtitle && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        fontSize: '0.75rem',
                        whiteSpace: 'pre-line',
                      }}
                    >
                      {metric.subtitle}
                    </Typography>
                  )}
                  {metric.chip && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <Chip
                        label={metric.chip}
                        size="small"
                        sx={{
                          bgcolor: alpha(theme.palette.success.main, 0.1),
                          color: theme.palette.success.main,
                          fontWeight: 600,
                          fontSize: '0.75rem',
                        }}
                      />
                      {metric.chipDetail && (
                        <Typography
                          variant="caption"
                          sx={{
                            ml: 1,
                            color: 'text.secondary',
                            fontSize: '0.75rem',
                          }}
                        >
                          {metric.chipDetail}
                        </Typography>
                      )}
                    </Box>
                      )}
                    </>
                  )}
                </Box>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette[metric.color].main, 0.1),
                    color: theme.palette[metric.color].main,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                      minWidth: 48,
                      height: 48,
                      flexShrink: 0,
                      '& svg': {
                        fontSize: '1.5rem',
                      },
                  }}
                >
                  {isCardsLoading ? (
                    <Skeleton variant="circular" width={32} height={32} />
                  ) : (
                    metric.icon
                  )}
                </Box>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default CallPerformanceSummary;
